import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchReservedMemberNos,
  generateReservedMemberNos,
  putReservedMemberNos,
  reservedCategoryLabel,
  type ReservedGenerateCategory,
  type ReservedMemberNoDto,
} from "../../../../api/memberNoRule";
import { getUserFacingMessage, reportError, showUserNotice } from "../../../../api/errors";

const GENERATE_OPTIONS: { key: ReservedGenerateCategory; label: string; hint: string }[] = [
  { key: "leopard", label: "豹子号", hint: "000111、001111、111111 等" },
  { key: "homophone", label: "谐音号", hint: "1314、1688、520 等" },
  { key: "straight", label: "连号", hint: "000123、012345、123456 等" },
  { key: "doublePair", label: "双连号", hint: "1122、112233（AABB 式）" },
];

type Props = {
  tenantLegacyId: string;
  digits: number;
  variant?: "page" | "embedded";
};

function UsageStatusBadge({ assigned }: { assigned: boolean }) {
  if (assigned) {
    return (
      <span className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-700">
        已使用
      </span>
    );
  }
  return (
    <span className="inline-flex shrink-0 items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700">
      未使用
    </span>
  );
}

export default function ReservedMemberNoPanel({ tenantLegacyId, digits, variant = "embedded" }: Props) {
  const [rows, setRows] = useState<ReservedMemberNoDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [manualTail, setManualTail] = useState("");
  const isPage = variant === "page";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchReservedMemberNos(tenantLegacyId);
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      reportError(e, "加载预留编号失败");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tenantLegacyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const enabledCount = useMemo(() => rows.filter((r) => r.isEnabled).length, [rows]);
  const assignedCount = useMemo(() => rows.filter((r) => r.isAssigned).length, [rows]);
  const freeCount = useMemo(() => rows.filter((r) => !r.isAssigned).length, [rows]);

  const saveRows = async (next: ReservedMemberNoDto[]) => {
    setBusy(true);
    try {
      await putReservedMemberNos(
        tenantLegacyId,
        next.map((r, idx) => ({
          sequenceTail: r.sequenceTail,
          category: r.category,
          note: r.note,
          sortOrder: idx,
          isEnabled: r.isEnabled,
        })),
      );
      setRows(next);
      showUserNotice("预留编号已保存。");
    } catch (e) {
      reportError(e, "保存失败");
    } finally {
      setBusy(false);
    }
  };

  const onGenerate = async (category: ReservedGenerateCategory) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await generateReservedMemberNos(tenantLegacyId, category);
      showUserNotice(r.addedCount > 0 ? `新增 ${r.addedCount} 个预留号` : "无新增（可能已存在）");
      await load();
    } catch (e) {
      reportError(e, "生成失败");
    } finally {
      setBusy(false);
    }
  };

  const onAddManual = () => {
    const tail = manualTail.replace(/\D/g, "").slice(-digits).padStart(digits, "0");
    if (!tail || /^0+$/.test(tail)) {
      showUserNotice("请输入有效序号尾段");
      return;
    }
    if (rows.some((r) => r.sequenceTail === tail)) {
      showUserNotice("已在列表中");
      return;
    }
    void saveRows([
      ...rows,
      {
        id: `new-${tail}`,
        sequenceTail: tail,
        category: "manual",
        note: "",
        sortOrder: rows.length,
        isEnabled: true,
        isAssigned: false,
      },
    ]);
    setManualTail("");
  };

  return (
    <div className={isPage ? "" : "bg-white rounded-2xl p-5 mt-6"}>
      <div className={`${isPage ? "bg-white rounded-2xl p-5" : ""}`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-bold text-gray-900">预留编号（序号尾段）</p>
            <p className="text-xs text-gray-400 mt-0.5">
              自动发号跳过预留尾段；已启用 {enabledCount} 个 · {digits} 位序号 · 仅超级管理员
            </p>
            {rows.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700">
                  未使用 {freeCount}
                </span>
                <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700">
                  已使用 {assignedCount}
                </span>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            disabled={loading || busy}
            onClick={() => void load()}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
          </button>
        </div>

        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-600 mb-2">按规则批量生成</p>
          <div className="flex flex-wrap gap-2">
            {GENERATE_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                disabled={busy}
                onClick={() => void onGenerate(opt.key)}
                className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-left hover:bg-gray-100 disabled:opacity-50"
              >
                <span className="block text-xs font-semibold text-gray-800">{opt.label}</span>
                <span className="block text-[11px] text-gray-400">{opt.hint}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          <input
            value={manualTail}
            onChange={(e) => setManualTail(e.target.value.replace(/\D/g, "").slice(0, digits))}
            placeholder={`手动添加 ${digits} 位尾段`}
            className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono"
          />
          <button
            type="button"
            disabled={busy}
            onClick={onAddManual}
            className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-medium disabled:opacity-50"
          >
            添加
          </button>
        </div>

        <div className={`overflow-y-auto space-y-2 ${isPage ? "max-h-[480px]" : "max-h-64"}`}>
          {loading ? (
            <p className="text-xs text-gray-400 py-4 text-center">加载中…</p>
          ) : rows.length === 0 ? (
            <p className="text-xs text-gray-400 py-4 text-center">暂无预留编号</p>
          ) : (
            rows.map((r) => (
              <div
                key={r.sequenceTail}
                className={`flex items-center justify-between rounded-xl border px-3 py-2.5 ${
                  r.isAssigned ? "border-amber-100 bg-amber-50/40" : "border-gray-100 bg-gray-50"
                }`}
              >
                <div className="min-w-0 flex-1 pr-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-mono text-sm font-bold text-gray-900">{r.sequenceTail}</p>
                    <UsageStatusBadge assigned={r.isAssigned} />
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">
                    {reservedCategoryLabel(r.category)}
                    {r.isAssigned ? ` · 已分配给 ${r.assignedMemberName || "会员"}` : " · 空闲可分配"}
                  </p>
                </div>
                {!r.isAssigned && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void saveRows(rows.filter((x) => x.sequenceTail !== r.sequenceTail))}
                    className="shrink-0 text-xs text-red-600 hover:underline disabled:opacity-50"
                  >
                    删除
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        <p className="text-[11px] text-gray-400 mt-3 leading-relaxed">
          入会申请自动编号会跳过以上尾段；手工指定预留号不会推进「下一序号」。已有会员改号与入会审核指定编号：只要未被占用即可（含大于下一序号），均不推进下一序号。
        </p>
      </div>
    </div>
  );
}
